import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import { Link } from "react-router-dom";
import axios from 'axios';
import TextField from '@mui/material/TextField';
import { getToken } from 'selectors/tokenSelector';
import config from "config/env";
import Button from "screens/components/basic/Button";
import AgGrid from "screens/components/advanced/AgGrid";

const DataGrid = ({ agGridRef, isComplete, verifyProduct, lockExpectedQuantity, deleteProduct, rows, scroll, canManageAudit, canStoreVerify, onRowSelection, ...props }) => {
    const params = useParams();
    const baseUrl = config.baseUrl();
    // redux state
    const common = useSelector(state => state.common || {});
    const entity = useSelector(state => state.entity || {});
    const { inventoryAudit } = entity;
    const { branchId } = common;
    const form = useSelector(state => state.form);

    // local state
    const [localRows, setLocalRows] = useState([]);
    const [isScannerInput, setIsScannerInput] = useState(false);
    const [searchText, setSearchText] = useState('');
    
    const canOwnerRecount = inventoryAudit.status === 3 && canManageAudit;
    const lastKeyTime = useRef(0);
    const barcodeBuffer = useRef("");
    const editedCellRef = useRef(null);
    const BARCODE_TIME_THRESHOLD = 15;
    
    useEffect(() => {
        if (inventoryAudit.status === 4 && !canManageAudit) {
            const needRecountRows = rows.filter(x => x.needRecount);
            setLocalRows(needRecountRows);
        }else{
            setLocalRows(rows);
        }
    }, [rows])
    
    const defaultColDef = {
        flex: 1,
    };

    const nameCell = ({ value, data }) => 
        <Link to={`/product/${data && data.id}`}>
            <span>{value}</span>
        </Link>;

    const skuCell = ({ value, data }) =>
        <Link to={`/product/${data && data.id}`}>
        <span>{value}</span>
        </Link>;

    const locationCell = ({ value }) => <span>{value}</span>;
    const warehouseTypeCell = ({ data }) => <span>{data && data.warehouseTypeName}</span>;
    const newquantityCell = ({ value, data }) => <span id={`Quantity_${data && data.id}`}>{value}</span>
    const expectedQtyCell = ({ data }) => {
        if (data){
            const formQty = data && data.newquantity;
            const expectedQty = lockExpectedQuantity && data && data.expectedQty != null ? data.expectedQty : data.inStock;
            const isDiff = form && formQty != null && !isNaN(formQty) && (formQty != expectedQty);
            const diff = isDiff && (formQty - expectedQty);
            const color = isDiff ? (diff > 0 ? 'green' : 'red') : 'black'
    
            return <span>{expectedQty} &nbsp;&nbsp;
                {isDiff && <Button label="adjust" onTouchTap={() => verifyProduct(data)} />}
            </span>
        }
    }

    const differenceQtyCell = ({ data }) => {
        if (data){
            const formQty = data && data.newquantity;
            const expectedQty = lockExpectedQuantity && data && data.expectedQty != null ? data.expectedQty : data.inStock;
            const isDiff = form && formQty != null && !isNaN(formQty) && (formQty != expectedQty);
            const diff = isDiff && (formQty - expectedQty);
            const color = isDiff ? (diff > 0 ? 'green' : 'red') : 'black'

            return <span style={{ color }}>{isDiff ? diff.toFixed(2) : ''}</span>
        }
    };

    const differenceCostCell = ({data }) => {
        if (data){
            const formQty = data && data.newquantity;
            const expectedQty = lockExpectedQuantity && data && data.expectedQty != null ? data.expectedQty : data.inStock;
            const isDiff = form && formQty != null && !isNaN(formQty) && (formQty != expectedQty);
            const diff = isDiff && (formQty - expectedQty);
            const diffCost = data && isDiff && diff * data.wac;
            const color = isDiff ? (diff > 0 ? 'green' : 'red') : 'black'

            return <span style={{ color }}>{isDiff ? diffCost.toFixed(2) : ''}</span>
        }
    };

    const notesCell = ({ value, data }) => <span id={`Notes_${data && data.id}`}>{value}</span>

    const deleteCell = ({ data }) => (
        !isComplete && data && <Button type="IconDeleteButton" className="IconDeleteButton" onTouchTap={() => deleteProduct(data)} />
    )

    const updateAuditProductQuantity = async (productId, warehouseType, quantity) => {
        // console.log(productId, warehouseType, quantity);
        if (quantity >= 0) {
            const model = {
                BranchId: branchId,
                AuditId: params ? params.id : 0,
                WarehouseType: warehouseType,
                ProductId: productId,
                Quantity: quantity != "" ? quantity : null,
                lockExpectedQuantity: inventoryAudit.lockExpectedQuantity
            }

            const token = getToken();
            try{
                const res = await axios.post(`${baseUrl}audit/updateAuditProductQuantity/${model.BranchId}/${model.AuditId}/${model.ProductId}/${model.Quantity}/${model.WarehouseType}`, 
                    model,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,  // Attach the token to the Authorization header
                        }
                    }
                );
                // console.log('API Response:', res);
            }catch(error){
                // console.error('Error updating data:', error);
            }
        }
    }

    const updateAuditProductNotes = async (productId, warehouseType, notes) => {
        if (notes.length >= 1) {
            const model = {
                BranchId: branchId,
                AuditId: params ? params.id : 0,
                WarehouseType: warehouseType,
                ProductId: productId,
                Notes: notes || ``
            }

            const token = getToken();
            try{
                const res = await axios.post(`${baseUrl}audit/updateAuditProductNotes`, 
                    model,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,  // Attach the token to the Authorization header
                        }
                    }
                );
                // console.log('API Response:', res);
            }catch(error){
                // console.error('Error updating data:', error);
            }
        }
    }

    const auditTableManagerColumns =  [
        { field: "name", headerName: "Product Name", sortable: true, minWidth: 250, suppressMenu: true, suppressContextMenu: true, cellRenderer: nameCell },
        { field: "sku", headerName: "SKU", sortable: true, suppressContextMenu: true, minWidth: 100, cellRenderer: skuCell },
        { field: "location", headerName: "Location", suppressContextMenu: true, minWidth: 100, cellRenderer: locationCell },
        { field: "warehouseType", headerName: "Warehouse", sortable: true, suppressContextMenu: true, minWidth: 110, cellRenderer: warehouseTypeCell },
        { field: "newquantity", headerName: "Counted Quantity", sortable: true, suppressContextMenu: true, editable: !isScannerInput && inventoryAudit.status !== 2, minWidth: 120, cellRenderer: newquantityCell },
        { field: "expectedQty", headerName: "Expected Quantity", sortable: false, suppressContextMenu: true, minWidth: 140, cellRenderer: expectedQtyCell },
        { field: "differenceQty", headerName: "Difference Quantity{$0}", sortable: false, suppressContextMenu: true, suppressMenu: true, minWidth: 120, cellRenderer: differenceQtyCell },
        { field: "differenceCost", headerName: "Difference Cost (From WAC){$1}", sortable: false, suppressContextMenu: true, suppressMenu: true, minWidth: 120, cellRenderer: differenceCostCell },
        { field: "notes", headerName: "Notes", sortable: false, minWidth: 100, suppressMenu: true, suppressContextMenu: true, editable: !isScannerInput && inventoryAudit.status !== 2, cellRenderer: notesCell },
        { field: "delete", headerName: "Remove Product", sortable: false, minWidth: 100, suppressMenu: true, suppressContextMenu: true, cellRenderer: deleteCell }
    ];

    const auditTableColumns =  [
        { field: "name", headerName: "Product Name", sortable: true, minWidth: 250, suppressMenu: true, suppressContextMenu: true, cellRenderer: nameCell },
        { field: "sku", headerName: "SKU", sortable: true, suppressContextMenu: true, minWidth: 100, cellRenderer: skuCell },
        { field: "location", headerName: "Location", suppressContextMenu: true, minWidth: 100, cellRenderer: locationCell },
        { field: "warehouseType", headerName: "Warehouse", sortable: true, suppressContextMenu: true, minWidth: 110, cellRenderer: warehouseTypeCell },
        { field: "newquantity", headerName: "Counted Quantity", sortable: true, suppressContextMenu: true, editable: !isScannerInput && inventoryAudit.status !== 2, minWidth: 120, cellRenderer: newquantityCell },
        { field: "notes", headerName: "Notes", sortable: false, minWidth: 100, suppressMenu: true, suppressContextMenu: true, editable: !isScannerInput && inventoryAudit.status !== 2, cellRenderer: notesCell },
        { field: "delete", headerName: "Remove Product", sortable: false, minWidth: 100, suppressMenu: true, suppressContextMenu: true, cellRenderer: deleteCell }
    ];

    const handleCellKeyDown = (event) => {
        const currentTime = new Date().getTime();
        const timeDifference = currentTime - lastKeyTime.current;
        lastKeyTime.current = currentTime;
    
        const nativeEvent = event.event;
        const key = nativeEvent.key;
    
        if (key.length === 1) {
          if (timeDifference < BARCODE_TIME_THRESHOLD) {
            barcodeBuffer.current += key; // Fast input → barcode detected
          } else {
            barcodeBuffer.current = key; // Slow typing → reset buffer
          }
        }

        if (key === "Enter") {
            nativeEvent.preventDefault(); // Prevent default enter key behavior
      
            // console.log("Detected Input:", barcodeBuffer.current);
      
            const isBarcode = barcodeBuffer.current.length > 4; //this is curusical Perry Barcode detection threshold
            const { api, column, node } = event;
            const enteredText = barcodeBuffer.current;
      
            if (isBarcode) {
                // console.log("Barcode detected, searching for model:", enteredText);
                const modelRow = rows.find((row) => row.model === enteredText);
      
                // Prevent modifying the cell (revert changes)
                agGridRef.current.api.stopEditing();
                //   nativeEvent.preventDefault();
                // console.log(editedCellRef.current);
                node.setDataValue(column.colId, editedCellRef.current);
      
              if (modelRow) {
                // Find the row index
                const rowIndex = rows.indexOf(modelRow);
      
                if (agGridRef.current) {
                  agGridRef.current.ensureIndexVisible(rowIndex); // Scroll to row
                  agGridRef.current.setFocusedCell(rowIndex,"newquantity");
      
                //   console.log("newquantity cell focused for model:", enteredText);
                }
              }
            }
      
            barcodeBuffer.current = ""; // Reset barcode buffer
          }
    };

    const onChangeSearch = (e) => {
        setSearchText(e.target.value);
        if (agGridRef && agGridRef.current){
            agGridRef.current.api.setGridOption("quickFilterText", e.target.value);
        }
    };

    // Function to clear all filters (including search)
    const clearAllFilters = () => {
        setSearchText(``);
        if (agGridRef && agGridRef.current) {
            agGridRef.current.api.setGridOption("quickFilterText", ``);
        }
    };

    let diff = ''
    let diffCost = ''
    if (canManageAudit) {
        const sum = x => x.reduce((p, c) => p + c, 0);
        const negtiveRows = rows.filter(x => x.diff && x.diff < 0);
        const negtiveSum = sum(negtiveRows.map(x => x.diff))
        const negtiveCostSum = sum(negtiveRows.map(x => x.diffCost))
        const positiveRows = rows.filter(x => x.diff && x.diff > 0);
        const positiveSum = sum(positiveRows.map(x => x.diff))
        const positiveCostSum = sum(positiveRows.map(x => x.diffCost))
        diff = `\n {${negtiveSum.toFixed(0)}, +${positiveSum.toFixed(0)}}`
        diffCost = `\n {${negtiveCostSum.toFixed(0)}, +${positiveCostSum.toFixed(0)}}`
    }
    let columnHeaders = canManageAudit ? auditTableManagerColumns : auditTableColumns;
    // columnHeaders = columnHeaders.map(x => ({ ...x, headerName: x.headerName.replace('{$0}', diff).replace('{$1}', diffCost) }))

    const rowSelection = useMemo(() => { 
        return { 
            mode: `multiRow`,
            checkboxes: true,  // Ensure this is being set correctly
            enableClickSelection: true,
        };
    }, []);

    // Grid options
    const gridOptions = {
        suppressAnimations: true,
        columnDefs: columnHeaders,
        rowData: rows,
        editable: !isScannerInput,
        suppressRowClickSelection: isScannerInput,
        suppressCellSelection: isScannerInput,
        suppressKeyboardEvent: isScannerInput,
        suppressContextMenu: true,
        getRowId: ({ data }) => `productId${data.id}`,
        onCellFocused: () => {
            setIsScannerInput(false);
        },
        onCellValueChanged: (event) => {
            const { oldValue, newValue, colDef, data } = event;

            if (colDef.field === "newquantity" && oldValue !== newValue) {
                updateAuditProductQuantity(data.id, data.warehouseType, newValue);
                if (data.newquantity !== data.expectedQty){
                    event.api.refreshCells({ force: true });
                }
            }

          if (colDef.field === "notes" && oldValue !== newValue) {
            updateAuditProductNotes(data.id, data.warehouseType, newValue)
            event.api.refreshCells({ force: true });
          }
        },
        onSelectionChanged: () => {
            if (agGridRef.current && agGridRef.current.api){
                const selectedNodes = agGridRef.current.api.getSelectedNodes();
                const selectedData = selectedNodes.map(node => node.data);
                onRowSelection(selectedData);
            }
        },
        onCellKeyDown: handleCellKeyDown
    };

    return (
        <React.Fragment>
            <div className="row">
                <div className="col-xs-5 col-sm-5 col-md-5 col-lg-5 col-xl-5 pb-2">
                    <TextField
                        type="text"
                        variant="standard"
                        value={searchText}
                        onChange={(e) => onChangeSearch(e)}
                        name='inventoryAuditSearch'
                        fullWidth={true}
                        label='Inventory Audit Product Search'
                        className="InventoryAuditName" />
                </div>
                <div className="col-xs-4 col-sm-3 col-md-3 col-lg-2 col-xl-2">
                    <Button
                        name='btnResetFilter'
                        className='mb-2'
                        label="Reset Filter"
                        primary={true}
                        onTouchTap={clearAllFilters}
                    />
                </div>
            </div>
            <div className="row">
                <div className="col-xs-12 pb-2">
                    <AgGrid
                        agGridRef={agGridRef}
                        columns={columnHeaders}
                        rows={localRows}
                        gridOptions={gridOptions}
                        rowSelection={agGridRef && canManageAudit ? rowSelection : undefined}
                        // AG Grid will just ignore rowSelection if it's undefined, but might fail when it's null
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

export default DataGrid;